import { requireById } from './dom'

/** Offsets within one ask-and-answer beat, in ms from the beat's start. */
const TYPING_AT = 600
const REPLY_AT = 1750
const PUBLISHED_AT = 2450
const CLEAR_AT = 4500
const BEAT_MS = 5100
/** Held after the last beat so the finished site can be read before it resets. */
const LOOP_GAP = 1700
/** How long the site takes to wind back to its starting state. */
const RESET_MS = 700

const HEADLINE_AFTER = 'Golden hour with the humpbacks'

interface Beat {
  /** Matches the data-act on the chip that jumps the loop to this beat. */
  act: string
  /** What the operator asks for. */
  ask: string
  /** How the assistant answers. */
  reply: string
  /** The edit the assistant then makes to the site behind the conversation. */
  apply: () => void
}

/**
 * The "edit your website by asking" showreel: a loop of asks plays over the
 * operator's site, and each answer lands as a visible change to the page.
 * The loop only runs while the stage is on screen, and the chips below jump
 * it to a given ask.
 */
export function initWebsiteChat(): void {
  const stage = requireById('wcstage')
  const chat = requireById('wcchat')
  const chips = requireById('chips')
  const list = requireById('wctours')
  const hero = requireById('wchero')
  const headline = requireById('wcheadline')
  const price = requireById('wcprice')
  const published = requireById('wcpub')

  // The markup holds the site's starting state; the loop winds back to it.
  const headlineBefore = headline.textContent ?? ''
  const priceBefore = price.textContent ?? ''
  const publishedBefore = published.textContent ?? ''

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  let timers: number[] = []
  let index = 0
  let playing = false

  function at(ms: number, run: () => void): void {
    timers.push(window.setTimeout(run, ms))
  }

  function clearTimers(): void {
    timers.forEach((id) => window.clearTimeout(id))
    timers = []
  }

  // ---- the site's three edits ----

  function addSunsetCruise(): void {
    const row = document.createElement('div')
    row.className = 'wcs-tour is-new enter'
    row.innerHTML =
      '<img src="/img/feed-07.jpg" alt="">' +
      '<div class="t-txt"><b><span class="t-name">Summer Sunset Cruise</span>' +
      '<span class="t-badge">NEW</span></b>' +
      '<small>2.5 hours · departs 6:30 pm through January</small></div>' +
      '<div class="t-buy"><span class="t-price">$120</span><span class="t-btn">Book</span></div>'
    list.prepend(row)
    // Two frames: one to commit the collapsed state, one to transition out of it.
    requestAnimationFrame(() => requestAnimationFrame(() => row.classList.remove('enter')))
  }

  function setPrice(text: string): void {
    price.textContent = text
    price.classList.remove('flash')
    // Reflow between remove/add so the roll-over animation restarts every time.
    void price.offsetWidth
    price.classList.add('flash')
  }

  function setHeadline(text: string): void {
    headline.classList.add('swapping')
    at(300, () => {
      headline.textContent = text
      headline.classList.remove('swapping')
    })
  }

  const beats: Beat[] = [
    {
      act: 'sunset',
      ask: 'Add a sunset cruise for January',
      reply:
        "Done. Summer Sunset Cruise is up: $120 for 2.5 hours, bookable across January. It's live on the site now.",
      apply: addSunsetCruise,
    },
    {
      act: 'price',
      ask: 'Drop the morning cruise to $85',
      reply:
        'Updated. The Morning Whale Cruise is $85 everywhere: the website, the booking widget and every connected channel.',
      apply: () => setPrice('$85'),
    },
    {
      act: 'hero',
      ask: 'Give the homepage a sunset feel',
      reply: "How's this? A warmer hero, and a headline to match. Say the word and I'll put it back.",
      apply: () => {
        hero.classList.add('sunset')
        setHeadline(HEADLINE_AFTER)
      },
    },
  ]

  function resetSite(animated: boolean): void {
    const added = list.querySelector('.is-new')
    if (added && animated) {
      added.classList.add('enter')
      at(RESET_MS, () => added.remove())
    } else {
      added?.remove()
    }
    price.textContent = priceBefore
    price.classList.remove('flash')
    hero.classList.remove('sunset')
    headline.classList.remove('swapping')
    headline.textContent = headlineBefore
    published.textContent = publishedBefore
    published.classList.remove('live')
    highlightChip(-1)
  }

  // ---- the conversation over the top ----

  function addBubble(kind: 'user' | 'bot', text: string): HTMLElement {
    const bubble = document.createElement('div')
    bubble.className = `wcb ${kind}`
    if (kind === 'bot') {
      const mark = document.createElement('img')
      mark.className = 'mark'
      mark.src = '/img/toura-logo.png'
      mark.alt = ''
      bubble.append(mark)
    }
    const body = document.createElement('span')
    body.textContent = text
    bubble.append(body)
    chat.append(bubble)
    return body
  }

  function addTyping(): HTMLElement {
    const body = addBubble('bot', '')
    body.innerHTML = '<span class="dots"><i></i><i></i><i></i></span>'
    return body
  }

  function clearBubbles(animated: boolean): void {
    const bubbles = [...chat.children]
    if (!animated) {
      chat.replaceChildren()
      return
    }
    bubbles.forEach((bubble) => bubble.classList.add('out'))
    at(450, () => bubbles.forEach((bubble) => bubble.remove()))
  }

  function markPublished(): void {
    published.textContent = 'Publishing…'
    published.classList.remove('live')
    at(PUBLISHED_AT - REPLY_AT, () => {
      published.textContent = 'Published just now ✓'
      published.classList.add('live')
    })
  }

  function highlightChip(active: number): void {
    chips.querySelectorAll('.chip').forEach((chip, i) => chip.classList.toggle('on', i === active))
  }

  // ---- the loop ----

  function playBeat(): void {
    const beat = beats[index]
    if (!beat) return

    const last = index === beats.length - 1
    highlightChip(index)
    addBubble('user', beat.ask)
    const answer = addTyping()

    at(TYPING_AT, () => answer.replaceChildren(beat.reply))
    at(REPLY_AT, () => {
      beat.apply()
      markPublished()
    })
    at(CLEAR_AT, () => clearBubbles(true))
    at(BEAT_MS, () => {
      if (last) resetSite(true)
      index = last ? 0 : index + 1
      at(last ? LOOP_GAP : 0, playBeat)
    })
  }

  function start(): void {
    if (playing) return
    playing = true
    clearTimers()
    clearBubbles(false)
    resetSite(false)
    index = 0
    playBeat()
  }

  // Wound back while it is off screen, so returning to the section never
  // catches the site mid-edit or shows it snapping back into place.
  function stop(): void {
    playing = false
    clearTimers()
    clearBubbles(false)
    resetSite(false)
  }

  // The showreel is only worth watching if it starts when the reader arrives,
  // rather than having finished somewhere above the fold.
  if (!still) {
    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) start()
          else stop()
        }
      },
      { threshold: 0.4 },
    ).observe(stage)
  }

  chips.addEventListener('click', (event) => {
    const chip = (event.target as HTMLElement).closest<HTMLButtonElement>('.chip')
    if (!chip) return

    const asked = beats.findIndex((beat) => beat.act === chip.dataset['act'])
    if (asked < 0) return

    clearTimers()
    clearBubbles(false)
    resetSite(false)
    index = asked
    playing = true
    playBeat()
  })

  // With motion turned off there is nothing to watch, so the site simply shows
  // what the conversation would have produced.
  if (still) {
    beats.forEach((beat) => beat.apply())
    published.textContent = 'Published just now ✓'
    published.classList.add('live')
    const beat = beats[beats.length - 1]
    if (beat) {
      addBubble('user', beat.ask)
      addBubble('bot', beat.reply)
    }
  }
}
